/**
 * CardDetailPanel — right-docked side panel for a single card.
 *
 * Notion behaviour (founder mandate, 2026-05-15 round 2):
 *   - NOT modal — no overlay, no backdrop blur, no focus trap. The
 *     board to the left stays fully interactive while the panel is
 *     open; clicking another card simply swaps the panel content
 *     (since the panel is keyed on `:cardId`, React replays state).
 *   - User-resizable: the left edge has a 4px drag-handle. The width
 *     is persisted to localStorage so it survives reloads.
 *   - Plain `<aside>` sitting as a flex sibling to the board in the
 *     route layout — no portal, no z-index gymnastics. Just a column
 *     in the flex row.
 *
 * Tabs (Chat / Artifacts / Audit / Members / Settings) and inline
 * title editing are unchanged from Phase 2 — only the chrome and the
 * containment model were rewritten.
 */
import type { Card, Column } from '@chaos/types/kanban';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Loader2, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { architectApi, architectKeys } from '../../lib/api/architect';
import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import { colorTint, normaliseColor } from '../../lib/status-color';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { ChatPane } from '../agent/chat-pane';
import { ModelPicker } from '../agent/model-picker';
import { ArchitectTab } from '../architect/architect-tab';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToaster } from '../ui/toast';
import { CardMembersTab } from './card-members-tab';

const TAB_VALUES = ['chat', 'artifacts', 'audit', 'members', 'architect', 'settings'] as const;
type TabValue = (typeof TAB_VALUES)[number];

interface CardDetailPanelProps {
  boardId: string;
  cardId: string;
}

function isTabValue(v: string | undefined): v is TabValue {
  return v !== undefined && (TAB_VALUES as readonly string[]).includes(v);
}

export function CardDetailSlideOver({ boardId, cardId }: CardDetailPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useSearch({ strict: false }) as { tab?: string };

  const activeTab: TabValue = isTabValue(search.tab) ? search.tab : 'chat';

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-card-panel-width',
    defaultWidth: 560,
    minWidth: 360,
    maxWidth: 960,
    side: 'right',
  });

  const cardQ = useQuery({
    queryKey: kanbanKeys.card(cardId),
    queryFn: () => kanbanApi.getCard(cardId),
  });

  const columnsQ = useQuery({
    queryKey: kanbanKeys.boardColumns(boardId),
    queryFn: () => kanbanApi.listColumns(boardId),
  });

  // Cheap probe — only renders the Architect tab if the card was spawned
  // by an Architect run. Backend returns `{ run: null }` for non-Architect
  // cards so the query stays warm but inert.
  const cardRunQ = useQuery({
    queryKey: architectKeys.cardRun(cardId),
    queryFn: () => architectApi.getCardRun(cardId),
  });
  const hasArchitectLineage = Boolean(cardRunQ.data?.run);

  const renameM = useMutation({
    mutationFn: (title: string) => kanbanApi.updateCard(cardId, { title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.card(cardId) });
      qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
    },
  });

  const closeToBoard = React.useCallback((): void => {
    navigate({ to: '/board/$boardId', params: { boardId } });
  }, [navigate, boardId]);

  const toaster = useToaster();

  const restoreM = useMutation({
    mutationFn: () => kanbanApi.restoreCard(cardId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
      await qc.invalidateQueries({ queryKey: kanbanKeys.card(cardId) });
      // Navigate back into the card we just unarchived so the user sees
      // their work materialise — feels more decisive than landing on the
      // board with the freshly-restored card silently re-appearing.
      void navigate({
        to: '/board/$boardId/card/$cardId',
        params: { boardId, cardId },
      });
    },
    onError: () => {
      toaster.toast({
        title: t('kanban.restore_failed'),
        variant: 'destructive',
        durationMs: 5_000,
      });
    },
  });

  const archiveM = useMutation({
    mutationFn: () => kanbanApi.deleteCard(cardId),
    onSuccess: (restored) => {
      qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
      closeToBoard();
      // Surface an Undo toast immediately. 5s is the standard window
      // (Notion / Gmail use the same range). After it auto-dismisses
      // the archive becomes "settled" and the user has to fish the
      // card out of an Archive view (Phase 5+).
      const cardTitle = restored?.title ?? cardQ.data?.title ?? t('kanban.archive_toast.fallback');
      toaster.toast({
        title: t('kanban.archive_toast.title'),
        description: cardTitle,
        durationMs: 5_000,
        action: {
          label: t('kanban.archive_toast.undo'),
          ariaLabel: t('kanban.archive_toast.undo_aria'),
          onClick: () => restoreM.mutate(),
        },
      });
    },
  });

  const setActiveTab = (tab: TabValue): void => {
    navigate({
      to: '/board/$boardId/card/$cardId',
      params: { boardId, cardId },
      search: { tab },
    });
  };

  // Escape closes the panel (keeps the keyboard-shortcut without forcing
  // a focus trap on the board).
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && document.activeElement?.tagName !== 'INPUT') {
        closeToBoard();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeToBoard]);

  // Inline title editing state.
  const [titleDraft, setTitleDraft] = React.useState('');
  const [editingTitle, setEditingTitle] = React.useState(false);
  React.useEffect(() => {
    if (cardQ.data) setTitleDraft(cardQ.data.title);
  }, [cardQ.data]);

  const submitTitle = async (): Promise<void> => {
    if (!cardQ.data) return;
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === cardQ.data.title) {
      setTitleDraft(cardQ.data.title);
      return;
    }
    await renameM.mutateAsync(trimmed);
  };

  const card: Card | undefined = cardQ.data;
  const columns: Column[] = columnsQ.data ?? [];
  const cardColumn = columns.find((col) => col.id === card?.columnId);

  return (
    <aside
      style={{ width }}
      className={cn(
        'relative flex h-full shrink-0 flex-col border-l border-border bg-background',
        isDragging && 'select-none',
      )}
      aria-label="Card details"
    >
      {/* Resize handle on the left edge */}
      <button
        type="button"
        aria-label="Resize panel"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute left-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0',
          'transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />

      {/* Header */}
      <header className="flex items-center justify-between gap-2 px-6 pt-5 pb-3">
        <div className="min-w-0 flex-1">
          {cardQ.isLoading ? (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </span>
          ) : card ? (
            <>
              {editingTitle ? (
                <input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      await submitTitle();
                    }
                    if (e.key === 'Escape') {
                      setTitleDraft(card.title);
                      setEditingTitle(false);
                    }
                  }}
                  onBlur={submitTitle}
                  // biome-ignore lint/a11y/noAutofocus: deliberate UX for inline edit.
                  autoFocus
                  className="w-full bg-transparent text-lg font-semibold tracking-tight focus:outline-none"
                  aria-label={t('kanban.card_title')}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingTitle(true)}
                  className="w-full truncate text-left text-lg font-semibold tracking-tight transition-colors hover:text-muted-foreground"
                >
                  {card.title}
                </button>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {cardColumn && <StatusPill column={cardColumn} />}
                {card.autonomyLevel && (
                  <span className="uppercase tracking-wide">{card.autonomyLevel}</span>
                )}
                <span>{new Date(card.createdAt).toLocaleString()}</span>
              </div>
            </>
          ) : (
            <span className="text-sm text-error">{t('kanban.card_not_found')}</span>
          )}
        </div>
        <button
          type="button"
          onClick={closeToBoard}
          aria-label={t('common.cancel')}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      {card && (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabValue)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList
            className={cn(
              'mx-6 mt-1 inline-flex w-auto self-start gap-1',
              hasArchitectLineage ? 'flex' : '',
            )}
          >
            <TabsTrigger value="chat">{t('kanban.tabs.chat')}</TabsTrigger>
            <TabsTrigger value="artifacts">{t('kanban.tabs.artifacts')}</TabsTrigger>
            <TabsTrigger value="audit">{t('kanban.tabs.audit')}</TabsTrigger>
            <TabsTrigger value="members">{t('kanban.tabs.members')}</TabsTrigger>
            {hasArchitectLineage && <TabsTrigger value="architect">Architect</TabsTrigger>}
            <TabsTrigger value="settings">{t('kanban.tabs.settings')}</TabsTrigger>
          </TabsList>

          <div className="flex min-h-0 flex-1 flex-col px-6 py-4">
            <TabsContent value="chat" className="mt-0 flex min-h-0 flex-1 flex-col">
              <ChatPane cardId={card.id} />
            </TabsContent>
            <TabsContent value="artifacts" className="mt-0">
              <PlaceholderTab
                title={t('kanban.tabs.artifacts')}
                subtitle={t('kanban.placeholders.artifacts')}
                nextPhase="Phase 4"
              />
            </TabsContent>
            <TabsContent value="audit" className="mt-0 flex flex-col gap-3">
              <AuditTimeline card={card} />
            </TabsContent>
            <TabsContent value="members" className="mt-0">
              <CardMembersTab cardId={card.id} />
            </TabsContent>
            {hasArchitectLineage && (
              <TabsContent value="architect" className="mt-0">
                <ArchitectTab cardId={card.id} boardId={card.boardId} />
              </TabsContent>
            )}
            <TabsContent value="settings" className="mt-0">
              <SettingsTab card={card} />
            </TabsContent>
          </div>

          <footer className="flex items-center justify-end px-6 py-3">
            <Button
              variant="ghost"
              size="sm"
              disabled={archiveM.isPending}
              onClick={() => archiveM.mutateAsync()}
              className="text-muted-foreground hover:text-error"
            >
              {archiveM.isPending ? t('common.loading') : t('kanban.archive_card')}
            </Button>
          </footer>
        </Tabs>
      )}
    </aside>
  );
}

function StatusPill({ column }: { column: Column }) {
  const colorKey = normaliseColor(column.color);
  const tint = colorTint(colorKey);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{
        backgroundColor:
          colorKey === 'default' ? 'hsl(var(--color-muted))' : `hsl(var(--status-${colorKey}-bg))`,
        color: colorKey === 'default' ? 'hsl(var(--color-foreground))' : tint.foreground,
      }}
    >
      <span
        aria-hidden
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: tint.foreground }}
      />
      {column.name}
    </span>
  );
}

function PlaceholderTab({
  title,
  subtitle,
  nextPhase,
}: {
  title: string;
  subtitle: string;
  nextPhase: string;
}) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
        {nextPhase}
      </span>
      <h4 className="text-base font-medium text-foreground">{title}</h4>
      <p className="max-w-xs">{subtitle}</p>
    </div>
  );
}

function AuditTimeline({ card }: { card: Card }) {
  const { t } = useTranslation();
  // Phase 2 audit timeline shows the rows derived from the card row itself.
  // Phase 6+ adds a server-side audit log query.
  const entries = [
    { ts: card.createdAt, label: t('kanban.audit.created') },
    ...(card.updatedAt && card.updatedAt !== card.createdAt
      ? [{ ts: card.updatedAt, label: t('kanban.audit.updated') }]
      : []),
    ...(card.archivedAt ? [{ ts: card.archivedAt, label: t('kanban.audit.archived') }] : []),
  ];
  return (
    <ol className="flex flex-col gap-3">
      {entries.map((e) => (
        <li key={`${e.ts}-${e.label}`} className="flex items-baseline gap-3 text-sm">
          <span className="font-medium text-foreground">{e.label}</span>
          <span className="text-xs text-muted-foreground">{new Date(e.ts).toLocaleString()}</span>
        </li>
      ))}
    </ol>
  );
}

function SettingsTab({ card }: { card: Card }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const updateM = useMutation({
    mutationFn: (autonomyLevel: Card['autonomyLevel']) =>
      kanbanApi.updateCard(card.id, { autonomyLevel }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.card(card.id) });
      qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(card.boardId) });
    },
  });

  const levels: Card['autonomyLevel'][] = ['HITL', 'HOTL', 'HOOL', 'agent_only'];

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h5 className="text-sm font-medium text-foreground">{t('kanban.settings.autonomy')}</h5>
        <p className="mt-1 text-xs text-muted-foreground">{t('kanban.settings.autonomy_help')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {levels.map((lvl) => (
            <Button
              key={lvl ?? 'inherit'}
              variant={card.autonomyLevel === lvl ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateM.mutateAsync(lvl)}
            >
              {lvl}
            </Button>
          ))}
          <Button
            variant={card.autonomyLevel === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateM.mutateAsync(null)}
          >
            {t('kanban.settings.inherit')}
          </Button>
        </div>
      </div>
      <div>
        <h5 className="text-sm font-medium text-foreground">{t('kanban.settings.preset')}</h5>
        <p className="mt-1 text-xs text-muted-foreground">{t('kanban.settings.preset_help')}</p>
        <div className="mt-3">
          <ModelPicker cardId={card.id} />
        </div>
      </div>
    </section>
  );
}
